import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@shared-ui/sheet";
import { Skeleton } from "@shared-ui/skeleton";
import {
	type UseQueryResult,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { DataTable } from "@/features/catalog/components/data-table";
import { PENDING_QUEUE_QUERY_OPTIONS } from "../pending-queue-options";
import {
	approvePortfolio,
	getPortfolioForReview,
	listPendingPortfolios,
	type ReviewablePortfolio,
	type ReviewablePortfolioProject,
	rejectPortfolio,
} from "../services/portfolio-approvals-api.service";
import { DecisionNotePanel } from "./decision-note-panel";
import { QueueLoadError } from "./queue-load-error";

const PENDING_PORTFOLIOS_KEY = ["approvals", "portfolios"] as const;

/** The pending portfolio queue. The page reads it for the count card, the queue for its rows. */
export function usePendingPortfolios(): UseQueryResult<ReviewablePortfolio[]> {
	return useQuery({
		queryKey: PENDING_PORTFOLIOS_KEY,
		queryFn: listPendingPortfolios,
		...PENDING_QUEUE_QUERY_OPTIONS,
	});
}

function portfolioTitle(portfolio: ReviewablePortfolio): string {
	return portfolio.title?.trim() || "Untitled portfolio";
}

/**
 * Every path into review (submitting, and the dev seed) sets `submittedAt`,
 * and the migration that adds the column also adds `review_status`, so no
 * pending portfolio predates it. The column is still nullable -- a draft that
 * was never submitted has none -- so fall back to the last change time.
 */
function submittedAt(portfolio: ReviewablePortfolio): string {
	return portfolio.submittedAt ?? portfolio.updatedAt;
}

function DetailField({
	label,
	children,
}: {
	label: string;
	children: ReactNode;
}) {
	return (
		<div className="grid gap-1">
			<span className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
				{label}
			</span>
			{children}
		</div>
	);
}

function ProjectCard({ project }: { project: ReviewablePortfolioProject }) {
	return (
		<li className="grid gap-2 rounded-2xl border border-slate-200 p-3">
			<div className="grid gap-0.5">
				<span className="font-medium">{project.title}</span>
				{project.category ? (
					<span className="text-xs text-muted-foreground">
						{project.category}
					</span>
				) : null}
			</div>
			<p className="leading-6 text-slate-700">{project.shortDescription}</p>
			<div className="grid grid-cols-3 gap-2">
				<img
					src={project.thumbnailUrl}
					alt={`${project.title} thumbnail`}
					className="aspect-square w-full rounded-lg object-cover"
				/>
				{project.imageUrls.map((url, index) => (
					<img
						key={url}
						src={url}
						alt={`${project.title}, ${index + 1} of ${project.imageUrls.length}`}
						className="aspect-square w-full rounded-lg object-cover"
					/>
				))}
			</div>
		</li>
	);
}

function PortfolioContent({ portfolioId }: { portfolioId: string }) {
	const detailQuery = useQuery({
		queryKey: ["approvals", "portfolio-detail", portfolioId],
		queryFn: () => getPortfolioForReview(portfolioId),
	});

	if (detailQuery.isLoading) {
		return <Skeleton className="h-64 w-full rounded-2xl" />;
	}

	if (!detailQuery.data) {
		return (
			<EmptyState
				title="This portfolio could not be loaded."
				action={
					<Button
						variant="outline"
						onClick={() => detailQuery.refetch()}
						isLoading={detailQuery.isFetching}
					>
						Try again
					</Button>
				}
			/>
		);
	}

	const { portfolio, projects } = detailQuery.data;

	return (
		<Card className="border-slate-200 shadow-none">
			<CardHeader>
				<CardTitle>Portfolio content</CardTitle>
				<CardDescription>
					The cover, projects and images the designer submitted.
				</CardDescription>
			</CardHeader>
			<CardContent className="grid gap-4 text-sm">
				<DetailField label="Designer">
					<span className="font-medium">
						{portfolio.designer.name}
						{portfolio.designer.firmName
							? ` (${portfolio.designer.firmName})`
							: ""}
					</span>
				</DetailField>
				<DetailField label="Submitted">
					<span>{new Date(submittedAt(portfolio)).toLocaleString()}</span>
				</DetailField>
				<DetailField label="Template">
					<span className="capitalize">{portfolio.template}</span>
				</DetailField>
				{portfolio.coverUrl ? (
					<DetailField label="Cover">
						<img
							src={portfolio.coverUrl}
							alt={`${portfolioTitle(portfolio)} cover`}
							className="aspect-video w-full rounded-2xl object-cover"
						/>
					</DetailField>
				) : null}
				<DetailField label={`Projects (${projects.length})`}>
					{projects.length ? (
						<ul className="grid gap-3">
							{projects.map((project) => (
								<ProjectCard key={project.id} project={project} />
							))}
						</ul>
					) : (
						<p className="text-muted-foreground">
							This portfolio has no projects.
						</p>
					)}
				</DetailField>
			</CardContent>
		</Card>
	);
}

/**
 * The note left by the last approve or reject decision. A resubmitted
 * portfolio keeps it, so the reviewer can see what was asked for last time.
 */
function PreviousReviewNote({ note }: { note: string }) {
	return (
		<section
			aria-labelledby="previous-review-note"
			className="grid gap-1 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm"
		>
			<h3 id="previous-review-note" className="font-medium">
				Note from the previous review
			</h3>
			<p className="whitespace-pre-wrap text-muted-foreground">{note}</p>
		</section>
	);
}

export function PortfolioReviewQueue() {
	const queryClient = useQueryClient();
	const portfoliosQuery = usePendingPortfolios();
	const [selected, setSelected] = useState<ReviewablePortfolio | null>(null);
	const [notes, setNotes] = useState("");

	const approveMutation = useMutation({
		mutationFn: (input: { id: string; notes?: string }) =>
			approvePortfolio(input.id, input.notes),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: PENDING_PORTFOLIOS_KEY,
			});
			toast.success("Portfolio approved");
			setSelected(null);
			setNotes("");
		},
	});

	const rejectMutation = useMutation({
		mutationFn: (input: { id: string; reason: string }) =>
			rejectPortfolio(input.id, input.reason),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: PENDING_PORTFOLIOS_KEY,
			});
			toast.success("Portfolio rejected");
			setSelected(null);
			setNotes("");
		},
	});

	const portfolios = portfoliosQuery.data;

	return (
		<>
			{portfoliosQuery.isLoading ? (
				<Skeleton className="h-64 w-full rounded-2xl" />
			) : portfoliosQuery.isError && !portfolios ? (
				<QueueLoadError
					onRetry={() => void portfoliosQuery.refetch()}
					isRetrying={portfoliosQuery.isFetching}
				/>
			) : !portfolios?.length ? (
				<EmptyState title="No portfolios are waiting for review." />
			) : (
				<DataTable
					rows={portfolios}
					rowKey={(portfolio) => portfolio.id}
					onRowActivate={(portfolio) => {
						setSelected(portfolio);
						setNotes("");
					}}
					rowLabel={(portfolio) => `Review ${portfolioTitle(portfolio)}`}
					columns={[
						{
							header: "Title",
							cell: portfolioTitle,
							cellClassName: "font-medium",
						},
						{
							header: "Designer",
							cell: (portfolio) => portfolio.designer.name,
						},
						{
							header: "Projects",
							cell: (portfolio) => portfolio.projectCount,
							align: "right",
							cellClassName: "tabular-nums",
						},
						{
							header: "Template",
							cell: (portfolio) => portfolio.template,
							cellClassName: "capitalize text-muted-foreground",
						},
						{
							header: "Submitted",
							cell: (portfolio) =>
								new Date(submittedAt(portfolio)).toLocaleDateString(),
							cellClassName: "text-muted-foreground",
						},
						{
							header: "Status",
							cell: (portfolio) => (
								<Badge variant="warning">{portfolio.reviewStatus}</Badge>
							),
						},
					]}
				/>
			)}

			<Sheet
				open={!!selected}
				onOpenChange={(open) => !open && setSelected(null)}
			>
				<SheetContent className="w-full overflow-y-auto sm:max-w-xl">
					{selected ? (
						<div className="grid gap-5">
							<SheetHeader>
								<SheetTitle>{portfolioTitle(selected)}</SheetTitle>
							</SheetHeader>
							{selected.reviewNotes ? (
								<PreviousReviewNote note={selected.reviewNotes} />
							) : null}
							<PortfolioContent portfolioId={selected.id} />
							<DecisionNotePanel
								description="Used as approval notes or rejection reason. The designer sees a rejection reason on their portfolio page."
								placeholder="Record why this portfolio is approved or rejected."
								notes={notes}
								onNotesChange={setNotes}
								onApprove={() =>
									approveMutation.mutate({ id: selected.id, notes })
								}
								onReject={() =>
									rejectMutation.mutate({ id: selected.id, reason: notes })
								}
								isApproving={approveMutation.isPending}
								isRejecting={rejectMutation.isPending}
							/>
						</div>
					) : null}
				</SheetContent>
			</Sheet>
		</>
	);
}
